import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Plus, 
  Clock, 
  BookOpen, 
  Users,
  Star,
  Play,
  Edit,
  Trash2,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLessonContext } from '../../contexts/LessonContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { lessonService } from '../../services/lessonService';
import { trackingService } from '../../services/trackingService';
import { Lesson } from '../../types';
import toast from 'react-hot-toast';
import { getGrades, getSubjects } from '../../utils/lessons';

const LessonCard: React.FC<{ lesson: Lesson }> = ({ lesson }) => {
  const { dispatch } = useLessonContext();
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [students, setStudents] = useState(0);

  useEffect(() => {
    const fetchRating = async () => {
      const summary = await trackingService.getLessonSummary(lesson.metadata.lesson_id);
      setRating(parseFloat((summary.averageScore * 5 / 100).toFixed(1)));
      setStudents(summary.totalStudents);
    };
    fetchRating();
  }, [lesson.metadata.lesson_id]);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm(t('lessonList.deleteConfirm').replace('{title}', lesson.metadata.title))) {
      try {
        await lessonService.deleteLesson(lesson.metadata.lesson_id);
        dispatch({ type: 'DELETE_LESSON', payload: lesson.metadata.lesson_id });
        toast.success(t('lessonList.lessonDeleted'));
      } catch (error) {
        console.error('Error deleting lesson:', error);
        toast.error(t('lessonList.deleteFailed'));
      }
    }
  };

  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="card hover:shadow-lg transition-all duration-200 cursor-pointer group"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
          {lesson.metadata.title}
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          {lesson.metadata.subject} • {t('lesson.grade')} {lesson.metadata.grade}
          {lesson.metadata.subconstruct && ` • ${lesson.metadata.subconstruct}`}
          {lesson.className && ` • ${lesson.className}`}
        </p>
        <p className="text-gray-700 line-clamp-2">{lesson.objectives[0] || t('lessonList.noDescription')}</p>
      </div>
      <div className="ml-4 flex items-center space-x-1">
        <Star className="w-4 h-4 text-yellow-400 fill-current" />
        <span className="text-sm text-gray-600">{rating}</span>
      </div>
    </div>

    <div className="flex flex-col gap-3">
      <div className="flex items-right justify-end space-x-2 sm:space-x-4 text-sm text-gray-500">
        <span className="flex items-center">
          <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
          <span className="hidden sm:inline">{lesson.metadata.duration_minutes} {t('lessonList.min')}</span>
          <span className="sm:hidden">{lesson.metadata.duration_minutes}m</span>
        </span>
        <span className="flex items-center">
          <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
          <span className="hidden sm:inline">{lesson.timeline.length} {t('lessonList.steps')}</span>
          <span className="sm:hidden">{lesson.timeline.length}s</span>
        </span>
        <span className="flex items-center">
          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
          <span className="hidden sm:inline">{students} {t('lessonList.students')}</span>
          <span className="sm:hidden">{students}</span>
        </span>
      </div>
      
      <div className="flex items-right justify-end space-x-2 w-full">
        <button
          onClick={handleDelete}
          className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title={t('lessonList.deleteLesson')}
        >
          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
        </button>
        <Link
          to={`/tracking/${lesson.metadata.lesson_id}`}
          className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title={t('lessonList.viewTrackingData')}
        >
          <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
        </Link>
        <Link
          to={`/lessons/${lesson.metadata.lesson_id}/edit`}
          className="btn-secondary flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-xs sm:text-sm flex-1 sm:flex-none"
        >
          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">{t('lessonList.edit')}</span>
        </Link>
        <Link
          to={`/session/${lesson.metadata.lesson_id}`}
          className="btn-primary flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-xs sm:text-sm flex-1 sm:flex-none"
        >
          <Play className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">{t('lessonList.start')}</span>
        </Link>
      </div>
    </div>
  </motion.div>
  );
};

export const LessonList: React.FC = () => {
  const { state, dispatch } = useLessonContext();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedGrade, setSelectedGrade] = useState('All');
  const [sortBy, setSortBy] = useState('recent');

  const subjects = getSubjects(t);
  const grades = getGrades(t);

  useEffect(() => {
    const fetchLessons = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const lessons = await lessonService.getAllLessons(user?.id);
        dispatch({ type: 'SET_LESSONS', payload: lessons });
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch lessons' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    fetchLessons();
  }, [dispatch, user?.id]);

  const filteredLessons = state.lessons.filter(lesson => {
        const matchesSearch = lesson.metadata.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.metadata.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lesson.className && lesson.className.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSubject = selectedSubject === 'All' || lesson.metadata.subject === selectedSubject;
    const matchesGrade = selectedGrade === 'All' || lesson.metadata.grade.toString() === selectedGrade;
    
    return matchesSearch && matchesSubject && matchesGrade;
  });

  const sortedLessons = [...filteredLessons].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.metadata.created_at).getTime() - new Date(a.metadata.created_at).getTime();
      case 'title':
        return a.metadata.title.localeCompare(b.metadata.title);
      case 'duration':
        return a.metadata.duration_minutes - b.metadata.duration_minutes;
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('lessonList.lessons')}</h1>
          <p className="text-gray-600">{t('lessonList.browseAndManage')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to="/lessons/library"
            className="btn-secondary flex items-center space-x-2"
          >
            <BookOpen className="w-4 h-4" />
            <span>{t('navigation.lessonLibrary')}</span>
          </Link>
          <Link
            to="/lessons/new"
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('lessonList.createLesson')}</span>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('lessonList.searchLessons')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 w-full"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center space-x-2">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="input-field flex-1"
              >
                <option value="All">{t('lessonList.all')}</option>
                {subjects.map(subject => (
                  <option key={subject.value} value={subject.value}>{subject.label}</option>
                ))}
              </select>
            </div>

            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="input-field flex-1 sm:w-20"
            >
              <option value="All">{t('lessonList.all')}</option>
              {grades.map(grade => (
                <option key={grade.value} value={grade.value}>{grade.label}</option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field flex-1 sm:w-32"
            >
              <option value="recent">{t('lessonList.mostRecent')}</option>
              <option value="title">{t('lessonList.titleAZ')}</option>
              <option value="duration">{t('lessonList.duration')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600">
          {filteredLessons.length} {filteredLessons.length === 1 ? t('lessonList.lessonFound') : t('lessonList.lessonsFound')}
        </p>
      </div>

      {/* Lesson Grid */}
      {state.loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-4 w-2/3"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            </div>
          ))}
        </div>
      ) : sortedLessons.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedLessons.map((lesson) => (
            <LessonCard key={lesson.metadata.lesson_id} lesson={lesson} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('lessonList.noLessonsFound')}</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedSubject !== t('lessonList.all') || selectedGrade !== t('lessonList.all')
              ? t('lessonList.adjustSearchCriteria')
              : t('lessonList.getStartedCreating')
            }
          </p>
          {!searchTerm && selectedSubject === t('lessonList.all') && selectedGrade === t('lessonList.all') && (
            <Link to="/lessons/new" className="btn-primary">
              {t('lessonList.createYourFirstLesson')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
